import type {
  GeoJSONFeature,
  GeoJSONFeatureCollection,
  GeoJSONGeometry,
  ValidationResult,
} from "./types";

export const validateGeometry = (
  geometry: any
): { isValid: boolean; error?: string } => {
  if (!geometry || typeof geometry !== "object") {
    return { isValid: false, error: "Geometry must be an object" };
  }

  const geometryType = geometry.type;
  if (geometryType !== "Polygon" && geometryType !== "MultiPolygon") {
    return {
      isValid: false,
      error: `Geometry must be a Polygon or MultiPolygon, found ${geometryType}`,
    };
  }

  if (
    !geometry.coordinates ||
    !Array.isArray(geometry.coordinates) ||
    geometry.coordinates.length === 0
  ) {
    return {
      isValid: false,
      error: "Geometry has invalid or empty coordinates",
    };
  }

  return { isValid: true };
};

export const validateFeature = (
  feature: any
): { isValid: boolean; error?: string } => {
  if (!feature.properties || typeof feature.properties !== "object") {
    return { isValid: false, error: "Feature must have properties" };
  }

  const geometryValidation = validateGeometry(feature.geometry);
  if (!geometryValidation.isValid) {
    return geometryValidation;
  }

  return { isValid: true };
};

export const validateGeoJSON = (content: string): ValidationResult => {
  try {
    const parsed = JSON.parse(content);

    // Handle FeatureCollection
    if (parsed.type === "FeatureCollection") {
      if (!Array.isArray(parsed.features) || parsed.features.length === 0) {
        return {
          isValid: false,
          error: "FeatureCollection must have at least one feature",
        };
      }

      for (let i = 0; i < parsed.features.length; i++) {
        const feature = parsed.features[i];
        const validation = validateFeature(feature);
        if (!validation.isValid) {
          return {
            isValid: false,
            error: `Feature ${i + 1}: ${validation.error}`,
          };
        }
      }

      return {
        isValid: true,
        data: parsed as GeoJSONFeatureCollection,
        type: "FeatureCollection",
      };
    }

    // Handle single Feature
    if (parsed.type === "Feature") {
      const validation = validateFeature(parsed);
      if (!validation.isValid) {
        return { isValid: false, error: validation.error };
      }

      return {
        isValid: true,
        data: parsed as GeoJSONFeature,
        type: "Feature",
      };
    }

    // Handle Geometry object
    if (parsed.type === "Polygon" || parsed.type === "MultiPolygon") {
      const validation = validateGeometry(parsed);
      if (!validation.isValid) {
        return { isValid: false, error: validation.error };
      }

      return {
        isValid: true,
        data: parsed as GeoJSONGeometry,
        type: "Geometry",
      };
    }

    return {
      isValid: false,
      error:
        "File must be a FeatureCollection, Feature, or Polygon/MultiPolygon geometry",
    };
  } catch (error) {
    return {
      isValid: false,
      error: error instanceof Error ? error.message : "Invalid GeoJSON file",
    };
  }
};
